import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  BarChart3,
  CalendarCheck,
  Eye,
  LockKeyhole,
  Mail,
  PackageCheck,
  ShieldCheck,
} from 'lucide-react';
import { KawaTechLogo } from '../branding/kawatech';

const featureItems = [
  {
    icon: PackageCheck,
    title: 'Operacao em fluxo continuo',
    description: 'Organize produtos, servicos, estoque e vendas com dados rastreaveis em tempo real.',
  },
  {
    icon: BarChart3,
    title: 'Gestao financeira conectada',
    description: 'Acompanhe faturamento, despesas, doacoes e indicadores em uma unica rotina.',
  },
  {
    icon: CalendarCheck,
    title: 'Visitas e clientes sob controle',
    description: 'Planeje agendas, carteiras e relacionamentos comerciais com clareza operacional.',
  },
  {
    icon: ShieldCheck,
    title: 'Seguro e confiavel',
    description: 'Controle multi-tenant, acesso por perfil e rotinas administrativas protegidas.',
  },
];

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Falha no login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-background lg:grid-cols-[41%_59%]">
      <section className="flex min-h-screen items-center justify-center px-6 py-12">
        <div className="w-full max-w-[430px]" data-testid="login-card">
          <div className="mb-11 text-center">
            <KawaTechLogo className="mx-auto h-9 w-auto" />
            <h1 className="mt-9 text-4xl font-extrabold tracking-normal text-secondary">
              KawaTenda
            </h1>
            <p className="mt-3 text-base text-primary/75">
              Acesse sua conta para continuar
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="font-bold text-foreground">
                Email
              </Label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-primary/55" />
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="email-input"
                  className="h-12 rounded-none border-0 bg-[#edf3ff] pl-12 pr-4 shadow-none focus-visible:ring-1 focus-visible:ring-primary"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="font-bold text-foreground">
                Senha
              </Label>
              <div className="relative">
                <LockKeyhole className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-primary/55" />
                <Input
                  id="password"
                  type="password"
                  placeholder="********"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="password-input"
                  className="h-12 rounded-none border-0 bg-[#edf3ff] px-12 shadow-none focus-visible:ring-1 focus-visible:ring-primary"
                />
                <Eye className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-primary/55" />
              </div>
            </div>

            {error && (
              <div className="bg-destructive/10 p-3 text-sm text-destructive" data-testid="error-message">
                {error}
              </div>
            )}

            <div className="flex items-center justify-between gap-4 text-sm text-primary">
              <label className="flex items-center gap-2">
                <input type="checkbox" className="h-4 w-4 rounded-none border-primary/40 accent-primary" />
                <span>Lembrar-me</span>
              </label>
              <Link to="/password-recovery" className="font-semibold hover:underline">
                Esqueceu a senha?
              </Link>
            </div>

            <Button
              type="submit"
              className="h-12 w-full rounded-md bg-primary text-base font-bold hover:bg-primary/95"
              disabled={loading}
              data-testid="login-button"
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </Button>
          </form>

          <div className="mt-8 space-y-3 text-center text-sm text-primary/70">
            <p>
              Nao tem uma conta?{' '}
              <Link to="/signup" className="font-bold text-primary hover:underline">
                Fale com o administrador
              </Link>
            </p>
            <p>
              Quer contratar o KawaTenda?{' '}
              <Link to="/onboarding" className="font-bold text-secondary hover:underline">
                Iniciar onboarding
              </Link>
            </p>
          </div>
        </div>
      </section>

      <section className="hidden min-h-screen items-center bg-primary px-20 py-16 text-white lg:flex">
        <div className="mx-auto w-full max-w-[660px]">
          <KawaTechLogo variant="negative" className="h-10 w-auto" />
          <h2 className="mt-16 max-w-[620px] text-5xl font-extrabold leading-tight tracking-normal">
            Transforme a gestao do seu negocio com o KawaTenda
          </h2>
          <p className="mt-5 max-w-[610px] text-lg leading-8 text-white">
            O sistema completo para vendas, estoque, faturamento, despesas, visitas e clientes em uma operacao integrada.
          </p>

          <div className="mt-14 space-y-9">
            {featureItems.map((item) => (
              <div key={item.title} className="grid grid-cols-[36px_1fr] gap-6">
                <item.icon className="mt-1 h-7 w-7 text-white" />
                <div>
                  <h3 className="text-2xl font-extrabold text-secondary">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-base leading-7 text-white">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 border-t border-white/25 pt-9">
            <p className="text-sm font-semibold text-white/90">
              A solucao completa para empresas que querem crescer com execucao organizada e gestao eficiente
            </p>
          </div>
        </div>
      </section>

      <section className="bg-primary px-6 py-10 text-white lg:hidden">
        <div className="mx-auto max-w-[430px]">
          <h2 className="text-2xl font-extrabold leading-tight">
            Gestao operacional e financeira conectada
          </h2>
          <p className="mt-3 text-sm leading-6 text-white">
            Controle vendas, estoque, faturamento, despesas, visitas e clientes em um unico WebApp.
          </p>
          <div className="mt-6 grid gap-4">
            {featureItems.slice(0, 2).map((item) => (
              <div key={item.title} className="flex gap-3">
                <item.icon className="mt-1 h-5 w-5 shrink-0 text-secondary" />
                <div>
                  <h3 className="font-bold text-secondary">{item.title}</h3>
                  <p className="mt-1 text-sm leading-6">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default LoginPage;
